import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import { CustomerSelect } from '@kwixeo/customers-vue'
import { BButton, BDropdownItem, BFormGroup, BFormRadioGroup, BFormRadio, BAlert, VBTooltip } from 'bootstrap-vue'
import { VueConstructor } from 'vue'
import { DateMixin, NumberMixin, Spinner, FormModal, emitter } from '@kwixeo/interface-vue'
import { KForm, KFormAddress, KFormCheckbox, KFormCheckboxGroup, KFormDatepicker, KFormSelect } from '@kwixeo/form-vue'
import moment from 'moment'
import { AxiosResponse } from 'axios'
import { mapActions, mapGetters, mapState } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

@Component({
  components: {
    CustomerSelect,
    Spinner, FormModal,
    KFormAddress, KFormCheckbox, KFormDatepicker, KForm, KFormCheckboxGroup, KFormSelect,
    BFormGroup, BFormRadioGroup, BFormRadio, BAlert, BButton,
    FontAwesomeIcon
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  methods: {
    ...mapActions('globalDatas', ['loadCountries']),
    ...mapActions('communication', ['loadModels'])
  },
  computed: {
    ...mapGetters('user', ['teamConfigs', 'team']),
    ...mapState('globalDatas', ['countries']),
    ...mapState('communication', ['models'])
  }
})
export default class InvoiceMultipleDocuments extends Mixins(NumberMixin, DateMixin) {
  team!: KwixeoFramework.Team
  teamConfigs!: KwixeoFramework.ConfigAttributes
  countries!: KwixeoFramework.Countries
  loadCountries!: (force?: boolean) => void
  loadModels!: (force?: boolean) => Promise<void>
  models!: KwixeoCommunication.Models

  @Prop({ required: true }) type!: KwixeoSales.DocumentType
  @Prop({ default: null }) customer!: KwixeoCustomers.Customer|null
  @Prop({ default: 'button' }) element!: 'button'|'dropdown'
  @Prop({ default: null }) label!: string|null
  @Prop({ default: null }) size!: string|null
  @Prop({ default: null }) variant!: string|null
  @Prop({ default: false }) disabled!: boolean
  @Prop({ default: false }) modal!: boolean

  private loading = false
  private modalState = false
  private multipleBill = false
  private processingMultiple = false
  private localCustomer: KwixeoCustomers.Customer|null = null
  private documentLoading = true
  private documents: KwixeoSales.Documents|KwixeoSales.MultipleBillSearchs = []
  private invoiceDate: string|Date|null = null
  private isDraft = true
  private addDocumentsInfos = true
  private sendInvoice = false
  private mailModel: number|null = null
  protected today: null|string = null
  private selectedDocuments: number[] = []
  private address: KwixeoSales.DocumentAddress = {}
  private jobUniqId: string|null = null
  private jobsUniqId: KwixeoSales.MultipleBillResults|null = null
  private errorMsg: string|null = null

  get component (): VueConstructor|null {
    switch (this.element) {
      case 'button':
        return BButton
      case 'dropdown':
        return BDropdownItem
    }
  }

  async mounted (): Promise<void> {
    this.loading = true
    await this.loadCountries()
    await this.loadModels()

    const today = moment().format('YYYY-MM-DD')
    this.today = today
    this.invoiceDate = today

    this.mailModel = this.teamConfigs.communication_model_invoice?.id ?? null
    this.loading = false
  }

  beforeDestroy (): void {
    emitter.off('multipleBillDone', this.multipleBillDone)
    emitter.off('multipleBillFailed', this.multipleBillFailed)
    emitter.off('mailSent', this.mailSent)
    emitter.off('documentSendFailed', this.mailFailed)
  }

  get needAddress (): boolean {
    if (this.selectedDocuments.length === 0) {
      return false
    }

    let hasAddress = false
    this.selectedDocuments.every(id => {
      const invoice = (this.documents as KwixeoSales.Documents).find(invoice => invoice.id === id) as KwixeoSales.Invoice
      if (invoice?.billing_address?.city && invoice?.billing_address?.postal_code) {
        hasAddress = true
        return false
      }
      return true
    })
    return !hasAddress
  }

  get minDate (): string|null {
    let closed_period: string|null = null
    if (this.team.sales['closed_period']) {
      closed_period = moment(this.team.sales['closed_period']).add(1, 'day').format('YYYY-MM-DD')
    }

    let min: string|null = null
    if (!this.teamConfigs.sales_disable_date_check) {
      min = this.team.sales['invoice_date']
    }

    if (min && (!closed_period || moment(min).isAfter(closed_period))) {
      return min
    } else if (closed_period) {
      return closed_period
    }

    return null
  }

  @Watch('isDraft')
  private watchIsDraft (): void {
    if (this.isDraft && this.sendInvoice) {
      this.sendInvoice = false
    }
  }

  @Watch('multipleBill')
  private async watchMultipleBill (): Promise<void> {
    if (this.multipleBill) {
      await this.loadCustomers()
    } else {
      await this.watchLocalCustomer()
    }
  }

  @Watch('modal', { immediate: true })
  private watchModal (state: boolean) {
    this.modalState = state
  }

  @Watch('modalState', { immediate: true })
  private watchModalState (state: boolean) {
    if (state) {
      emitter.on('multipleBillDone', this.multipleBillDone)
      emitter.on('multipleBillFailed', this.multipleBillFailed)
      emitter.on('mailSent', this.mailSent)
      emitter.on('documentSendFailed', this.mailFailed)
      this.watchMultipleBill()
    } else {
      emitter.off('multipleBillDone', this.multipleBillDone)
      emitter.off('multipleBillFailed', this.multipleBillFailed)
      emitter.off('mailSent', this.mailSent)
      emitter.off('documentSendFailed', this.mailFailed)
    }
    this.errorMsg = null
    this.processingMultiple = false
    this.selectedDocuments = []
  }

  @Watch('customer', { immediate: true })
  private watchCustomer (customer: KwixeoCustomers.Customer|null): void {
    this.localCustomer = customer
  }

  @Watch('localCustomer', { immediate: true })
  private async watchLocalCustomer (): Promise<void>
  {
    if (!this.localCustomer) {
      this.documents = []
      return
    }
    await this.loadDocuments()
  }

  // Get documents to bill
  private async loadCustomers (): Promise<void> {
    this.documents = []
    this.documentLoading = true
    this.$kwSales.axios.get('/sales/' + this.type  + '/bill-multiple-search')
      .then(response => {
        this.documents = response.data
        this.documentLoading = false
      })
      .catch(error => {
        throw new Error(error)
      })
  }

  // Get documents to bill
  private async loadDocuments (): Promise<void> {
    this.documents = []
    this.documentLoading = true
    this.$kwSales.axios.get('/sales/' + this.type, {
      params: {
        filter: {
          customer: this.localCustomer?.id,
          is_draft: false,
          can_be_billed: true,
          main_currency: true
        }
      }
    })
      .then(response => {
        this.documents = response.data.data
        this.documentLoading = false
      })
      .catch(error => {
        throw new Error(error)
      })
  }

  private modalHide (): void {
    if (!this.customer) {
      this.localCustomer = null
    }
  }

  private async submitComplete (response: AxiosResponse): Promise<void> {
    if (this.multipleBill) {
      this.jobsUniqId = response.data.map((job: { uniq_id: string; customer_id: number}) => ({
        uniq_id: job.uniq_id,
        data: (this.documents as KwixeoSales.MultipleBillSearchs).find(document => document.customer_id === job.customer_id),
        status: false,
        error: null,
        send_status: false,
        send_error: null,
        invoice_id: null
      }))
      this.processingMultiple = true
    } else {
      this.jobUniqId = response.data
    }
  }

  private async multipleBillDone (datas: KwixeoSales.MultipleBillEvent): Promise<void> {
    if (this.multipleBill) {
      const element = this.jobsUniqId?.find(job => job.uniq_id === datas.uniq_id)
      if (element) {
        element.status = true
        element.invoice_id = datas.invoice_id
      }
    } else {
      this.redirectInvoice(datas)
    }
  }

  private async multipleBillFailed (datas: KwixeoSales.MultipleBillFailedEvent): Promise<void> {
    if (this.multipleBill) {
      const element = this.jobsUniqId?.find(job => job.uniq_id === datas.uniq_id)
      if (element) {
        element.status = true
        element.error = datas.error
      }
    } else if (datas.uniq_id == this.jobUniqId) {
      this.errorMsg = datas.error
    }
  }

  private mailSent (event: KwixeoCommunication.MailSentEvent): void {
    const element = event.elements.find(element => {
      const subIndex = this.jobsUniqId?.findIndex(job => job.invoice_id === element.id && element.type === 'invoice') ?? -1
      return subIndex >= 0
    })
    if (element) {
      const job = this.jobsUniqId?.find(job => job.invoice_id === element.id)
      if (job) {
        job.send_status = true
      }
    }
  }

  private mailFailed (event: KwixeoSales.DocumentSendFailedEvent): void {
    const element = event.elements.find(element => {
      const subIndex = this.jobsUniqId?.findIndex(job => job.invoice_id === element.id && element.type === 'invoice') ?? -1
      return subIndex >= 0
    })
    if (element) {
      const job = this.jobsUniqId?.find(job => job.invoice_id === element.id)
      if (job) {
        job.send_status = true
        job.send_error = event.error
      }
    }
  }

  private async redirectInvoice (datas: KwixeoSales.MultipleBillEvent): Promise<void> {
    if (datas.uniq_id !== this.jobUniqId) {
      return
    }

    await this.$router.push({
      name: 'invoice-item',
      params: {
        id: String(datas.invoice_id)
      }
    })
  }

  private selectAll (state: boolean): void {
    if (!state) {
      this.selectedDocuments = []
    } else {
      this.selectedDocuments = (this.documents as KwixeoSales.Documents).map(document => document.id)
    }
  }

  private selectAllCustomers (state: boolean): void {
    if (!state) {
      this.selectedDocuments = []
    } else {
      this.selectedDocuments = (this.documents as KwixeoSales.MultipleBillSearchs).map(document => document.customer_id)
    }
  }
}
